import { ReactNode, useEffect, useState } from "react"
import { PanelRightOpen, PhoneOff, UsersRound } from "lucide-react"
import { NetworkQualityIcon, describeNetworkThreshold, networkThresholdTone, type NetworkThreshold } from "./NetworkPanel"

export const formatCallDuration = (durationMs: number) => {
  const totalSeconds = Math.max(0, Math.floor(durationMs / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (value: number) => String(value).padStart(2, "0")
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
}

export type MeetingClockState = { status: "upcoming" | "live" | "ended"; label: string; accessibleLabel: string }

export const describeMeetingClock = (now: number, startIso?: string | null, endIso?: string | null): MeetingClockState => {
  const start = startIso ? Date.parse(startIso) : Number.NaN
  const end = endIso ? Date.parse(endIso) : Number.NaN
  if (!Number.isNaN(start) && now < start) {
    const label = `Starts in ${formatCallDuration(start - now)}`
    return { status: "upcoming", label, accessibleLabel: label }
  }
  if (!Number.isNaN(start) && !Number.isNaN(end) && end > start) {
    const elapsed = formatCallDuration(now - start)
    const duration = formatCallDuration(end - start)
    const overtime = now > end
    return {
      status: now >= end ? "ended" : "live",
      label: `${elapsed} / ${duration}${overtime ? " · overtime" : ""}`,
      accessibleLabel: `${elapsed} elapsed of ${duration} scheduled${overtime ? `; ${formatCallDuration(now - end)} overtime` : ""}`,
    }
  }
  if (!Number.isNaN(start)) {
    const label = `${formatCallDuration(now - start)} elapsed`
    return { status: "live", label, accessibleLabel: label }
  }
  return { status: "live", label: "In progress", accessibleLabel: "In progress" }
}

type WorkspaceHeaderProps = {
  title: string
  startTime?: string | null
  endTime?: string | null
  participantCount: number
  onLeave: () => void
  panelOpen: boolean
  onTogglePanel: () => void
  unreadCount: number
  badges?: ReactNode
  compact: boolean
  /**
   * Phone layouts keep only the title, clock, count, and media badges: the controls bar already
   * has Tools and Leave, and network quality moves into its More menu.
   */
  mobile?: boolean
  inCall: boolean
  networkThreshold?: NetworkThreshold
  networkOpen?: boolean
  onToggleNetwork?: () => void
  /** The breakout room the attendee is in, if any. */
  roomLabel?: string | null
}

/**
 * WiseFriend workspace header: meeting title and clock, Daily-derived participant count, media
 * badges, network quality, panel toggle, and Leave.
 */
export function WorkspaceHeader({
  title,
  startTime,
  endTime,
  participantCount,
  onLeave,
  panelOpen,
  onTogglePanel,
  unreadCount,
  badges,
  compact,
  mobile = false,
  inCall,
  networkThreshold = "good",
  networkOpen = false,
  onToggleNetwork,
  roomLabel = null,
}: WorkspaceHeaderProps) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(intervalId)
  }, [])
  const clock = describeMeetingClock(now, startTime, endTime)
  // Before joining there is no controls bar, so Leave stays in the header at every size.
  const showToolbar = inCall && !mobile

  return (
    <header
      data-testid="call-workspace-header"
      className="flex shrink-0 items-center gap-3 border-b border-white/10 bg-[#0f1419] px-3 py-2 text-white sm:px-4"
      style={{ paddingTop: "max(0.5rem, env(safe-area-inset-top))" }}
    >
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <h1 className="truncate text-sm font-semibold sm:text-base">{title}</h1>
          {roomLabel && (
            <span data-testid="call-room-pill" className="inline-flex shrink-0 items-center rounded-full bg-sky-400/20 px-2 py-0.5 text-[11px] font-semibold text-sky-100">
              {roomLabel}
            </span>
          )}
        </div>
        <div className="mt-0.5 flex min-w-0 items-center gap-x-3 text-xs text-white/60">
          <span data-testid="call-clock" role="timer" aria-label={clock.accessibleLabel} title={clock.accessibleLabel} className="min-w-0 truncate tabular-nums">{clock.label}</span>
          <span data-testid="meeting-participant-count" aria-label={`${participantCount} ${participantCount === 1 ? "participant" : "participants"}`} className="inline-flex shrink-0 items-center gap-1">
            <UsersRound aria-hidden="true" className="h-3.5 w-3.5" />
            {participantCount}{!compact && ` ${participantCount === 1 ? "participant" : "participants"}`}
          </span>
          {badges && <span className="shrink-0">{badges}</span>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {showToolbar && onToggleNetwork && (
          <button
            type="button"
            onClick={onToggleNetwork}
            aria-pressed={networkOpen}
            aria-label={`Network quality: ${describeNetworkThreshold(networkThreshold)}`}
            title={`Network quality: ${describeNetworkThreshold(networkThreshold)}`}
            data-testid="call-network-button"
            className={`flex h-9 w-9 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 ${networkThresholdTone(networkThreshold)}`}
          >
            <NetworkQualityIcon threshold={networkThreshold} className="h-4 w-4" />
          </button>
        )}
        {showToolbar && (
          <button
            type="button"
            onClick={onTogglePanel}
            aria-pressed={panelOpen}
            aria-label={panelOpen ? "Hide meeting tools" : "Show meeting tools"}
            data-testid="call-toggle-panel"
            className="relative flex h-9 items-center gap-1.5 rounded-full bg-white/10 px-3 text-xs font-semibold hover:bg-white/20"
          >
            <PanelRightOpen className="h-4 w-4" />
            {!compact && <span>Tools</span>}
            {unreadCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#d64545] px-1 text-[10px] font-bold">
                {unreadCount}
              </span>
            )}
          </button>
        )}
        {(!inCall || !mobile) && (
          <button
            type="button"
            onClick={onLeave}
            aria-label="Leave meeting"
            data-testid="call-header-leave"
            className="flex h-9 items-center gap-1.5 rounded-full bg-[#d64545] px-3 text-xs font-semibold text-white hover:bg-[#c03a3a]"
          >
            <PhoneOff className="h-4 w-4" />
            {!compact && <span>Leave</span>}
          </button>
        )}
      </div>
    </header>
  )
}
